import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Grid, Image } from 'semantic-ui-react'

import { FORM_1120_S_SCREENS, Form1120sProps } from '.'
import { GridRowColumn, Text } from '@/componentsV1/BaseComponents'
import { useReselector } from '@/utils/sharedHooks'
import { selectTaxListQuestionResponsesByQuestionIds } from '@/features/taxes/annual-taxes/tax-checklist/taxChecklist.selectors'
import ReviewSubSection from '../shared/ReviewSubSection'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { FormFlowFooter } from '@/components/form-flow/FormFlowFooter'
import {
  getBusinessQuestionIds,
  getPersonalQuestionIds,
} from '../shared/personal-details/CheckYourDetailsPanel'
import { updateDetailsQuestionsIds } from './UpdateDetailsPanel'
import { TAX_ENTITY_TYPES } from '../../../taxConstants'
import { TaxListQuestionId } from '../service'
import { useNavigateWithLocation } from '@/utils/routeHelpers'
import { selectTqFormIsReadOnly } from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import CommentArea from '@/features/taxes/annual-taxes/components/comment-area'
import { useReviewPanelComment } from '../../helpers'
import { SubStepIdentifiers } from '../shared/review-steps-and-progresses/stepProgress.helpers'
import { uncommonScenarioQuestionIds } from './UncommonScenariosPanel'
import { tqBigSpace } from '../helpers'

const Form1120SReviewPanel = ({ goBack, previousScreen }: Form1120sProps) => {
  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const navigate = useNavigateWithLocation()
  const { formId } = useParams()
  const readOnly = useReselector(selectTqFormIsReadOnly, formId)
  const uncommonScenariosResponses = useReselector(
    selectTaxListQuestionResponsesByQuestionIds,
    uncommonScenarioQuestionIds.filter(
      (qId) =>
        qId === TaxListQuestionId.sold_fixed_assets ||
        qId === TaxListQuestionId.engaged_in_real_estate ||
        qId === TaxListQuestionId.scorp_owned_other_businesses
    ),
    Number(formId)
  )
  const showFollowScenarios = useMemo(() => {
    return uncommonScenariosResponses.some(
      (response) => response.value === true
    )
  }, [uncommonScenariosResponses])
  const { comment, saveComment, updateComment } = useReviewPanelComment(
    SubStepIdentifiers.checkDetails1120s
  )

  return (
    <Grid>
      <GridRowColumn centerContent {...tqBigSpace}>
        <Image src="https://heard-images.s3.amazonaws.com/assets/magnifying-glass-checkmark.svg" />
      </GridRowColumn>
      <GridRowColumn {...tqBigSpace}>
        <Text as="display2" textAlign="center">
          Review
        </Text>
      </GridRowColumn>
      {!readOnly && (
        <GridRowColumn {...tqBigSpace}>
          <Text as="bodyLg">
            Review your answers for accuracy. Click edit to go back to the
            previous section.
          </Text>
        </GridRowColumn>
      )}
      {readOnly && (
        <GridRowColumn {...tqBigSpace}>
          <Text as="bodyLg">
            You can no longer edit your Business Tax Questionnaire responses,
            but you can view them at anytime.
          </Text>
        </GridRowColumn>
      )}
      <Grid.Row />
      <ReviewSubSection
        title="confirm personal details"
        questionIds={getPersonalQuestionIds(TAX_ENTITY_TYPES.form_1120_s)}
        editScreen={FORM_1120_S_SCREENS.confirmDetails}
      />
      <ReviewSubSection
        title="confirm business details"
        questionIds={getBusinessQuestionIds(TAX_ENTITY_TYPES.form_1120_s)}
        editScreen={FORM_1120_S_SCREENS.confirmDetails}
      />
      <ReviewSubSection
        title={`update details for ${taxYear}`}
        questionIds={updateDetailsQuestionsIds}
        editScreen={FORM_1120_S_SCREENS.updateDetails}
      />
      <ReviewSubSection
        title="Less common scenarios"
        questionIds={uncommonScenarioQuestionIds}
        editScreen={FORM_1120_S_SCREENS.uncommonScenarios}
      />
      {!readOnly && (
        <GridRowColumn {...tqBigSpace} short>
          <CommentArea
            label="Add a comment (optional)"
            placeholder="Add a note or ask a question to provide additional context for your tax preparer"
            value={comment}
            onChange={updateComment}
          />
        </GridRowColumn>
      )}
      {!readOnly && (
        <FormFlowFooter
          isSubmit
          onBack={() => {
            saveComment()
            goBack(
              showFollowScenarios
                ? (previousScreen ?? null)
                : FORM_1120_S_SCREENS.uncommonScenarios
            )
          }}
          onForward={() => navigate('/taxes/annual/tax_checklist/')}
        />
      )}
    </Grid>
  )
}

export default Form1120SReviewPanel
